import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/system';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import SessionStatusPill from 'Pages/EditCourseSessions/Components/SessionStatusPill';
import { formatSessionDate, formatSessionTime } from 'Pages/EditCourseSessions/utils/session-datetime';
import { attendancePercentageColor } from 'Pages/EditCourseSessions/utils/session-labels';
import type { SessionsReportResponseI } from 'Api/axios/sessions-report-api';

interface Props {
  report: SessionsReportResponseI | null;
  loading: boolean;
  error: string | null;
  showIndividualAttendance: boolean;
  onRowClick?: (sessionId: string, courseId: string) => void;
}

const styles: Record<string, SxProps<Theme>> = {
  accordion: {
    borderRadius: 2,
    border: `1px solid ${colors.GRAY_HR}`,
    boxShadow: 'none',
    overflow: 'hidden',
    mb: 1.5,
    '&::before': { display: 'none' },
  },
  summary: { px: 2, '& .MuiAccordionSummary-content': { my: 1 } },
  groupTitle: { fontSize: 14, fontWeight: 600, color: colors.BLACK },
  groupChip: {
    height: 22,
    fontSize: 11,
    fontWeight: 500,
    bgcolor: colors.BLUE_GRAY,
    color: colors.GRAY_DARKER,
    border: 'none',
  },
  details: { p: 0 },
  headerCell: {
    bgcolor: colors.BLUE_GRAY,
    fontSize: 11,
    fontWeight: 600,
    color: colors.GRAY_DARK,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    py: 1,
  },
  row: { cursor: 'pointer', '&:hover': { bgcolor: colors.custom.table.row.hover } },
  empty: { textAlign: 'center', py: 6, color: colors.GRAY_DARK, fontSize: 13 },
};

export default function SessionsReportGroupedList({
  report,
  loading,
  error,
  showIndividualAttendance,
  onRowClick,
}: Props) {
  const { t2 } = useTranslation('SessionsReport');

  if (loading && !report) {
    return <LinearProgress sx={{ height: 3, borderRadius: 99 }} />;
  }

  if (error) {
    return (
      <Box sx={styles.empty}>
        <Typography sx={{ color: colors.RED, fontSize: 13 }}>{error}</Typography>
      </Box>
    );
  }

  if (!report || report.groups.length === 0) {
    return (
      <Box sx={styles.empty}>
        <Typography>{t2(showIndividualAttendance ? 'employeeHistoryEmpty' : 'empty')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {report.groups.map(group => (
        <Accordion key={group.groupKey} defaultExpanded sx={styles.accordion} disableGutters>
          <AccordionSummary expandIcon={<ExpandIcon />} sx={styles.summary}>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ width: '100%' }}>
              <Typography sx={styles.groupTitle}>{group.groupLabel}</Typography>
              <Chip
                label={t2('groupMeta.sessionsCount', [String(group.groupMeta.sessionsCount)])}
                size="small"
                sx={styles.groupChip}
              />
              <Chip
                label={t2('groupMeta.totalHours', [String(group.groupMeta.totalHours)])}
                size="small"
                sx={styles.groupChip}
              />
              <Chip
                label={t2('groupMeta.attendancePercentage', [String(group.groupMeta.attendancePercentage)])}
                size="small"
                sx={{
                  ...styles.groupChip,
                  bgcolor: `${attendancePercentageColor(group.groupMeta.attendancePercentage)}1A`,
                  color: attendancePercentageColor(group.groupMeta.attendancePercentage),
                }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={styles.details}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.headerCell}>{t2('table.session')}</TableCell>
                  <TableCell sx={styles.headerCell}>{t2('table.course')}</TableCell>
                  <TableCell sx={styles.headerCell}>{t2('table.date')}</TableCell>
                  <TableCell sx={styles.headerCell}>{t2('table.time')}</TableCell>
                  <TableCell sx={styles.headerCell}>{t2('table.instructor')}</TableCell>
                  <TableCell sx={styles.headerCell}>{t2('table.location')}</TableCell>
                  <TableCell sx={styles.headerCell}>
                    {showIndividualAttendance ? t2('table.individualAttendance') : t2('table.attendance')}
                  </TableCell>
                  <TableCell sx={styles.headerCell}>{t2('table.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.sessions.map(s => (
                  <TableRow key={s.id} sx={styles.row} onClick={() => onRowClick?.(s.id, s.courseId)}>
                    <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>{s.title}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: colors.GRAY_DARKER }}>{s.courseName}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                      {formatSessionDate(s.startDateTime)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{formatSessionTime(s.startDateTime)}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: colors.GRAY_DARKER }}>{s.instructorName}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: colors.GRAY_DARKER }}>{s.location || '—'}</TableCell>
                    <TableCell>
                      {showIndividualAttendance && s.userAttendance ? (
                        <SessionStatusPill status={s.userAttendance.status || null} variant="attendance" size="sm" />
                      ) : (
                        <Box sx={{ fontSize: 12.5 }}>
                          <Box component="span" sx={{ color: colors.GRAY_DARKER }}>
                            {s.attendance.present}/{s.attendance.total}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 600,
                              ml: 0.75,
                              color: attendancePercentageColor(s.attendance.percentage),
                            }}
                          >
                            {s.attendance.percentage}%
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <SessionStatusPill status={s.status} size="sm" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
